/**
 * A small, type-based icon set for accounting records — not a port of
 * legacy webledger's per-leaf-account icon scheme (one hand-picked SVG per
 * account, ~100 files keyed by full account path via `icon_map.json`). Those
 * icon *files* were never present anywhere reachable (gitignored uploads
 * directory, empty on disk) — only the path-to-filename mapping survived,
 * so there was nothing to reuse. A fixed icon per account **type** plus one
 * for a placeholder (folder) account covers the same visual need — telling
 * rows apart at a glance — without depending on an upload mechanism or a
 * hand-curated mapping that would need maintaining forever after.
 *
 * All monochrome, `currentColor`-based (theme-aware for free, same as
 * `fieldHistory.ts`'s `SHEETS_ICON`), 16x16.
 */

export type AccountIconKind = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE' | 'FOLDER';

const ICONS: Record<AccountIconKind, string> = {
  // Wallet — cash on hand.
  ASSET: '<rect x="1.5" y="4" width="13" height="9" rx="1.5"/><path d="M1.5 6.5h13" fill="none" stroke="currentColor"/><circle cx="11.5" cy="9.5" r="1.1" fill="var(--cf-surface,#fff)" stroke="currentColor"/>',
  // Card — money owed.
  LIABILITY: '<rect x="1.5" y="3.5" width="13" height="9" rx="1.5"/><path d="M1.5 6h13" fill="none" stroke="currentColor"/><rect x="3" y="8.5" width="4" height="1.6" fill="var(--cf-surface,#fff)" stroke="none"/>',
  // Scale — net worth balance.
  EQUITY: '<path d="M8 2v11M4 13h8" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2.5 5h5M8.5 5h5" fill="none" stroke="currentColor" stroke-width="1.1"/><path d="M2.5 5l-1.5 3.5a2 2 0 0 0 3 0L2.5 5zM13.5 5l-1.5 3.5a2 2 0 0 0 3 0L13.5 5z" fill="none" stroke="currentColor" stroke-width="1.1"/>',
  // Arrow into a tray — money coming in.
  INCOME: '<path d="M8 1.5v8M5 6.5l3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M1.5 10.5v2.5a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-2.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>',
  // Receipt/cart — money going out.
  EXPENSE: '<path d="M3.5 1.5h9l-.7 9.5a1.2 1.2 0 0 1-1.2 1.1H5.4a1.2 1.2 0 0 1-1.2-1.1L3.5 1.5z" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M6 4.5v5M10 4.5v5" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/>',
  // Folder — a placeholder (structural) account.
  FOLDER: '<path d="M1.5 4.5a1 1 0 0 1 1-1h3.4l1.2 1.4h6.4a1 1 0 0 1 1 1v6.2a1 1 0 0 1-1 1H2.5a1 1 0 0 1-1-1V4.5z"/>',
};

/** 16x16 inline SVG for an account row/list — `isPlaceholder` always wins (a folder's own type is secondary to "this is structure, not a bookable account"). */
export function accountIconHtml(type: string, isPlaceholder: boolean, label?: string): string {
  const kind: AccountIconKind = isPlaceholder ? 'FOLDER' : (ICONS[type as AccountIconKind] ? (type as AccountIconKind) : 'ASSET');
  const title = label ? `<title>${label}</title>` : '';
  return (
    `<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="${label ? 'false' : 'true'}" ` +
    `class="cf-acct-icon" style="display:inline-block;vertical-align:-3px;color:var(--cf-ink-soft,#5c6a7a)">` +
    `${title}${ICONS[kind]}</svg>`
  );
}
